import type { LevelConfig } from "../level_config.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { authGetConditional } from "../api/http_client.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import type { ReleaseLevel } from "../level_pack/level_pack_service.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { isConfigMissing } from "../utils/network_errors.js";

const CONVEYOR_LEVELS_CACHE_KEY = "conveyor_levels_v1";

interface ConveyorLevelsSource
{
  release_uid: string;
  level_from: number;
  level_to: number;
}

interface ConveyorLevelsCache extends ConveyorLevelsSource
{
  version: number | null;
  levels: ReleaseLevel[];
}

interface LevelBatchBlob
{
  uid: string;
  version: number;
  levels: ReleaseLevel[];
}

function parseSource( raw: unknown ): ConveyorLevelsSource | null
{
  if ( !raw || typeof raw !== "object" || Array.isArray( raw ) )
    return null;

  const value = raw as Record<string, unknown>;
  const from = Number( value.level_from );
  const to = Number( value.level_to );

  if ( typeof value.release_uid !== "string" || value.release_uid === "" )
    return null;
  if ( !Number.isInteger( from ) || !Number.isInteger( to ) || from < 1 || to < from )
    return null;

  return { release_uid: value.release_uid, level_from: from, level_to: to };
}

function sameSource( a: ConveyorLevelsSource, b: ConveyorLevelsSource ): boolean
{
  return a.release_uid === b.release_uid && a.level_from === b.level_from && a.level_to === b.level_to;
}

function toConfigs( cache: ConveyorLevelsCache | null ): LevelConfig[]
{
  return ( cache?.levels ?? [] ).flatMap( entry => LevelPackService.toConfig( entry ) ?? [] );
}

export class ConveyorLevelsService
{
  private static cache = PersistentCache.read<ConveyorLevelsCache>( CONVEYOR_LEVELS_CACHE_KEY );
  private static configs = toConfigs( ConveyorLevelsService.cache );
  private static refreshing: Promise<void> | null = null;

  static hasCachedConfig(): boolean
  {
    return this.cache !== null;
  }

  public static getConfigs(): LevelConfig[]
  {
    return this.configs;
  }

  public static refresh(): Promise<void>
  {
    if ( this.refreshing ) return this.refreshing;

    this.refreshing = this.load()
      .catch( error =>
      {
        if ( isConfigMissing( error ) )
        {
          this.apply( null );
          return;
        }
        console.warn( "[ConveyorLevels] failed to refresh, keeping current levels", error );
      } )
      .finally( () => { this.refreshing = null; } );
    return this.refreshing;
  }

  private static async load(): Promise<void>
  {
    const source = parseSource( await getContentConfig<unknown>( "conveyor_levels" ) );
    if ( !source )
    {
      console.warn( "[ConveyorLevels] config carries no usable release_uid / level range" );
      this.apply( null );
      return;
    }

    const cached = this.cache && sameSource( this.cache, source ) ? this.cache : null;
    const etag = cached?.version !== null && cached?.version !== undefined
      ? `"${source.release_uid}-l${source.level_from}-${source.level_to}-${cached.version}"`
      : null;
    const result = await authGetConditional<LevelBatchBlob>(
      `api/player/level-release/${source.release_uid}/levels?from=${source.level_from}&to=${source.level_to}`,
      etag
    );

    if ( result.status === 304 )
      return;

    const blob = result.data;
    if ( !blob || !Array.isArray( blob.levels ) )
    {
      console.warn( `[ConveyorLevels] release ${source.release_uid} returned no level list` );
      return;
    }

    this.apply( { ...source, version: typeof blob.version === "number" ? blob.version : null, levels: blob.levels } );
    console.info( `[ConveyorLevels] ${this.configs.length} level(s) from ${source.release_uid} ${source.level_from}..${source.level_to}` );
  }

  private static apply( cache: ConveyorLevelsCache | null ): void
  {
    this.cache = cache;
    this.configs = toConfigs( cache );

    if ( cache )
      PersistentCache.write( CONVEYOR_LEVELS_CACHE_KEY, cache );
    else
      PersistentCache.remove( CONVEYOR_LEVELS_CACHE_KEY );
  }
}
